import { string } from 'yup';

/**
 * Input: snakeCaseExample
 * Output: SnakeCaseExample
 * Output (withSpace): Snake Case Example
 */
export const camelCaseToTitleCase = (value: string, withSpace = false) =>
  value.replace(/([A-Z])/g, withSpace ? ' $1' : '$1').replace(/^./, (match) => match.toUpperCase());

/**
 * Input: waiting_customer / waiting-customer
 * Output: Waiting Customer
 */
export function stringKeyToTitleCase(value: string): string {
  return value
    .toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Input: example
 * Output: Example
 */
export const capitalize = (value: string, lowerRest = false) =>
  value.charAt(0).toUpperCase() + (lowerRest ? value.slice(1).toLowerCase() : value.slice(1));

/**
 * Input: buildMessage('example.{{interpolation}}', { interpolation: 'replaceWithMe' })
 * Output: example.replaceWithMe
 */
export function interpolate(message = '', options: Record<string, any> = {}): string {
  return Object.keys(options).reduce(
    (acc, key) => acc.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), options[key] || key),
    message,
  );
}

/**
 * Input: F1C85E
 * Output: #F1C85E
 */
export function ensureHexColor(value: string): string {
  if (!value || value.startsWith('#')) {
    return value;
  }
  return `#${value}`;
}

export function buildPisanoTitle(value: string): string {
  if (!value) {
    return 'Pisano';
  }
  return `${value} | Pisano`;
}

export function getNameInitials(name: string, count = 2): string {
  if (!name) {
    return 'X';
  }
  return name
    .split(' ')
    .slice(0, count) // Take first two items
    .map((item: string) => item.charAt(0))
    .join('');
}

export function escapeHtmlEntities(value: string) {
  if (!value) {
    return '';
  }

  return value.replace(/&(amp|apos|lt|gt|quot);/g, (_, match) => {
    switch (match) {
      case 'amp':
        return '&';
      case 'apos':
        return "'";
      case 'lt':
        return '<';
      case 'gt':
        return '>';
      case 'quot':
        return '\\';
    }
  });
}

export function buildEndpointWithQuery(value: string, query: string) {
  if (!query) {
    return value;
  }

  return `${value}?${query}`;
}

export function buildMailToLink(values: Record<string, string>) {
  return (
    'mailto:?' +
    Object.entries(values)
      .map(([key, value]) => `${key}=${value}`, '')
      .join('&')
  );
}

export function isValidHex(color: string) {
  if (!color || typeof color !== 'string') {
    return false;
  }

  // Validate hex values
  if (color.substring(0, 1) === '#') {
    color = color.substring(1);
  }

  switch (color.length) {
    case 3:
      return /^[0-9A-F]{3}$/i.test(color);
    case 6:
      return /^[0-9A-F]{6}$/i.test(color);
    case 8:
      return /^[0-9A-F]{8}$/i.test(color);
    default:
      return false;
  }
}

export function truncateString(str: string, maxLength: number = 20, suffix?: string): string {
  try {
    if (str.length <= maxLength) {
      return str;
    } else {
      return str.length <= maxLength ? str : str.slice(0, maxLength) + (suffix || '');
    }
  } catch (e) {
    console.error(e, 'str', str, 'maxLength', maxLength);
    return str;
  }
}

export function breakTextForPieChart(text: string, maxLineLength: number = 10): string {
  try {
    if (!text || text.length <= maxLineLength) {
      return text;
    }

    if (text.length > 20) {
      return text;
    }

    if (text.includes('&')) {
      const parts = text.split('&');
      if (parts.length === 2) {
        const beforeAmpersand = parts[0].trim();
        const afterAmpersand = parts[1].trim();

        if (
          beforeAmpersand.length > 0 &&
          beforeAmpersand.length <= maxLineLength &&
          afterAmpersand.length <= maxLineLength
        ) {
          return `${beforeAmpersand}\n& ${afterAmpersand}`;
        }
      }
    }

    const andMatch = text.match(/(.+?)\s+(and\s+.+)/i);
    if (andMatch && andMatch[1] && andMatch[2]) {
      const beforeAnd = andMatch[1].trim();
      const fromAnd = andMatch[2].trim();

      if (beforeAnd.length <= maxLineLength && fromAnd.length <= maxLineLength) {
        return `${beforeAnd}\n${fromAnd}`;
      }
    }

    const words = text.split(' ');
    if (words.length === 2) {
      return `${words[0]}\n${words[1]}`;
    }

    return text;
  } catch (e) {
    console.error('breakTextForPieChart error:', e, 'text:', text);
    return text;
  }
}

export function getOptimalFontSizeForPieChart(text: string, baseSize: number = 14, maxLength: number = 12): number {
  if (!text || text.length <= maxLength) {
    return baseSize;
  }

  const ratio = maxLength / text.length;
  const minSize = Math.max(baseSize * 0.6, 10);
  const calculatedSize = baseSize * ratio;

  return Math.max(calculatedSize, minSize);
}

export function isValidUrl(value: string): boolean {
  return string().url().isValidSync(value);
}

export function isValidEmail(value: string): boolean {
  return string().email().isValidSync(value);
}

// Loose, as in legacy: an optional +, an optional bracketed code, then digits with spaces, dashes, dots or slashes.
const PHONE_NUMBER_PATTERN = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/;

export function isValidPhoneNumber(value: string): boolean {
  return PHONE_NUMBER_PATTERN.test(value);
}

export function isValidRegex(value: string): boolean {
  if (!value) return false;

  try {
    new RegExp(value);
    return true;
  } catch {
    return false;
  }
}
